import { axios } from '@vyce/core/src/api';

import { Company, RequestPositionDataData, Testimonial, UserPreviewData } from '../../types';
import {
  GetUserData,
  CreateCompanyPhotoData,
  CreateTestimonial,
  CompanyRolesResponseDTO,
  UpdateStaffPermissionsPayload,
  ListStaffRequest,
  ListCompanyWorkersRequest,
  GetStaffResponse,
  GetCompanyWorkersResponse,
  StatusMessage,
  AutoUserDeletionSettings,
  CompanySettingsDTO,
  ExpiringDocumentsSettings,
  ExpiringDocumentsSettingPayload,
  ExpiringDocumentsSettingsListResponse,
} from '../types';
import config from '../config';

const URL = `${config.gatewayApiUrl}`;

export const getCompanySettingsRequest = (companyId: string) => {
  return axios.get<CompanySettingsDTO>(`${URL}/public/v1/companies/${companyId}/settings`);
};

export const updateAutoUserDeletionRequest = (companyId: string, payload: AutoUserDeletionSettings) => {
  return axios.post<AutoUserDeletionSettings>(
    `${URL}/public/v1/companies/${companyId}/settings/time/auto_user_deletion`,
    payload
  );
};

const EXPIRING_DOCUMENTS_SETTINGS_PATH = (companyId: string) =>
  `${URL}/public/v1/companies/${companyId}/settings/notifications/expiring_documents`;

export const getExpiringDocumentsSettingsRequest = (companyId: string) => {
  return axios.get<ExpiringDocumentsSettingsListResponse>(EXPIRING_DOCUMENTS_SETTINGS_PATH(companyId));
};

export const createExpiringDocumentsSettingRequest = (
  companyId: string,
  payload: ExpiringDocumentsSettingPayload
) => {
  return axios.post<ExpiringDocumentsSettings>(EXPIRING_DOCUMENTS_SETTINGS_PATH(companyId), payload);
};

export const updateExpiringDocumentsSettingRequest = (
  companyId: string,
  settingId: string,
  payload: ExpiringDocumentsSettingPayload
) => {
  return axios.put<ExpiringDocumentsSettings>(
    `${EXPIRING_DOCUMENTS_SETTINGS_PATH(companyId)}/${settingId}`,
    payload
  );
};

export const deleteExpiringDocumentsSettingRequest = (companyId: string, settingId: string) => {
  return axios.delete(`${EXPIRING_DOCUMENTS_SETTINGS_PATH(companyId)}/${settingId}`);
};

export const createCompanyWithOwnerRequest = (data: RequestPositionDataData) => {
  return axios.post<Company>(`${URL}/public/v1/companies/create_company_with_owner`, data);
};

export const listAvailableRolesRequest = (companyId: string) => {
  return axios.get<CompanyRolesResponseDTO>(`${URL}/public/v1/companies/${companyId}/roles/available`);
};

export const updateStaffPermissionsRequest = (
  companyId: string,
  userId: string,
  data: UpdateStaffPermissionsPayload
) => {
  return axios.post<StatusMessage>(
    `${URL}/public/v1/companies/${companyId}/users/${userId}/permissions`,
    data
  );
};

export const getStaffRequest = (companyId: string, payload: ListStaffRequest) => {
  return axios.post<GetStaffResponse>(`${URL}/public/v1/companies/${companyId}/staff`, {
    ...payload,
    order_by: [{ field: 'last_name', desc: false }],
  });
};

export const getCompanyWorkersfRequest = (companyId: string, payload: ListCompanyWorkersRequest) => {
  return axios.post<GetCompanyWorkersResponse>(`${URL}/public/v1/companies/${companyId}/workers`, payload);
};

export const removeStaffMemberRequest = (companyId: string, userId: string) => {
  return axios.delete<StatusMessage>(`${URL}/public/v1/companies/${companyId}/users/${userId}/staff`);
};

// Company-scoped counterpart of the Legend `/admin/v1/users/{userId}/core` route. Same payload,
// except `user.employee.bank_account` is always nulled — only Legend staff read bank details.
export const getCompanyUserCoreRequest = ({ companyId, userId }: GetUserData) => {
  return axios.get<UserPreviewData>(`${URL}/public/v1/companies/${companyId}/users/${userId}/core`);
};

// Core api

export const getCompanyRequest = (token: string, companyId: string) => {
  return axios.get(`/companies/${companyId}?short=false`);
};

export const getCompanyExtendedDataRequest = (token: string, companyId: string) => {
  return axios.get(`/companies/${companyId}/extended`);
};

export const updateCompanyRequest = (token: string, companyId: string, data: Company) => {
  return axios.patch(`/companies/${companyId}`, data);
};

export const saveLogoRequest = (file: File, token: string, companyId: string) => {
  const bodyFormData = new FormData();
  bodyFormData.append('file', file);
  return axios.post(`/companies/${companyId}/logo`, bodyFormData);
};

export const getPassportByUserIdRequest = (token: string, companyId: string, userId: string) => {
  return axios.get(`/companies/${companyId}/users/${userId}/documents/passport`);
};

export const createPhotoRequest = (token: string, companyId: string, data: CreateCompanyPhotoData) => {
  const bodyFormData = new FormData();
  const { file, title, description } = data;
  if (file) {
    bodyFormData.append('file', file);
  }
  bodyFormData.append('title', title);
  if (description) {
    bodyFormData.append('description', description);
  }
  return axios.post(`/companies/${companyId}/photos_album/photos`, bodyFormData);
};

export const updatePhotoRequest = (
  token: string,
  companyId: string,
  photoId: string,
  data: CreateCompanyPhotoData
) => {
  const bodyFormData = new FormData();
  const { file, title, description } = data;
  if (file) {
    bodyFormData.append('file', file);
  }
  if (title) {
    bodyFormData.append('title', title);
  }
  if (description) {
    bodyFormData.append('description', description);
  }
  return axios.patch(`/companies/${companyId}/photos_album/photos/${photoId}`, bodyFormData);
};

export const deletePhotosRequest = (token: string, companyId: string, photoIds: string[]) => {
  return axios.delete(`/companies/${companyId}/photos_album/photos/bulk_delete`, {
    data: { photo_ids: photoIds },
  });
};

export const createTestimonialRequest = ({ companyId, token, testimonial, file }: CreateTestimonial) => {
  const bodyFormData = new FormData();
  if (file) {
    bodyFormData.append('file', file);
  }
  bodyFormData.append('testimonial', testimonial.testimonial);
  bodyFormData.append('public', 'true');
  bodyFormData.append('author_name', testimonial.author_name);
  bodyFormData.append('author_position', testimonial.author_position);
  bodyFormData.append('author_company', testimonial.author_company);

  return axios.post(`/companies/${companyId}/testimonials`, bodyFormData);
};

export const updateTestimonialRequest = (
  token: string,
  companyId: string,
  testimonial: Partial<Testimonial>
) => {
  return axios.patch(`/companies/${companyId}/testimonials/${testimonial.uuid}`, {
    public: testimonial.public,
  });
};

export const switchTestimonialRequest = (token: string, companyId: string, testimonialId: string) => {
  return axios.patch(`/companies/${companyId}/testimonials/${testimonialId}/switch`);
};

export const deleteTestimonialRequest = (token: string, companyId: string, id: string) => {
  return axios.delete(`/companies/${companyId}/testimonials/${id}`);
};
